import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Plus, Trash2, Save, Loader2, Workflow, X, ChevronUp, ChevronDown,
  Activity, Check, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Admin sequences (combo) builder.
 *
 * A sequence is an ORDERED list of cues that the engine plays back-to-back —
 * e.g. "Jab → Cross → Slip inside → Cross". Repeats are allowed. The runner
 * picks sequences MORE often than random cues (~65%) but caps each sequence at
 * 3 firings per round. No mode/level filter — admin curates the cue list per
 * sequence to fit the workout.
 */
export default function RoninSequencesAdmin() {
  const [sequences, setSequences] = useState([]);
  const [cues, setCues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // sequence being edited, or {name:"",cue_ids:[]} for new
  const [diagnosingId, setDiagnosingId] = useState(null); // expanded diagnostic row

  const load = async () => {
    setLoading(true);
    try {
      const [sqRes, cuesRes] = await Promise.all([
        api.get("/admin/ronin-box/sequences"),
        api.get("/admin/ronin-box/cues"),
      ]);
      setSequences(sqRes.data || []);
      setCues((cuesRes.data || []).filter((c) => c.active));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(load, 0);
    return () => clearTimeout(t);
  }, []);

  const cueById = useMemo(() => {
    const m = {};
    for (const c of cues) m[c.id] = c;
    return m;
  }, [cues]);

  const toggleActive = async (seq) => {
    try {
      await api.patch(`/admin/ronin-box/sequences/${seq.id}`, { active: !seq.active });
      setSequences((arr) => arr.map((s) => (s.id === seq.id ? { ...s, active: !seq.active } : s)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Toggle failed");
    }
  };

  const remove = async (seq) => {
    if (!window.confirm(`Delete sequence "${seq.name}"? Cannot be undone.`)) return;
    try {
      await api.delete(`/admin/ronin-box/sequences/${seq.id}`);
      setSequences((arr) => arr.filter((s) => s.id !== seq.id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="border-b border-[#27272A] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/admin/ronin-box" className="text-zinc-400 hover:text-white transition-colors" data-testid="back-to-ronin-admin">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Ronin Box</p>
            <h1 className="font-display text-2xl uppercase tracking-tight">Sequences</h1>
          </div>
        </div>
        <Button onClick={() => setEditing({ name: "", cue_ids: [], active: true })} className="bg-[#FF331F] hover:bg-[#FF331F]/85 text-white font-bold uppercase tracking-widest text-xs" data-testid="sequences-new">
          <Plus className="h-4 w-4 mr-2" /> New sequence
        </Button>
      </div>

      <div className="p-6 max-w-5xl mx-auto">
        <p className="text-sm text-zinc-400 mb-5 max-w-2xl">
          Pre-built combos the engine plays in order — e.g. <span className="text-zinc-200 font-bold">Jab → Cross → Slip inside → Cross</span>. The runner picks sequences <span className="text-zinc-200">more often than random cues (~65% of slots)</span> but caps each sequence at <span className="text-zinc-200 font-bold">3 firings per round</span>. Repeats inside a sequence are allowed.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-zinc-500">
            <Loader2 className="h-5 w-5 mr-2 animate-spin" /> Loading…
          </div>
        ) : sequences.length === 0 ? (
          <div className="text-center py-16 border border-[#27272A] border-dashed rounded-md bg-[#0A0A0A]">
            <Workflow className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 mb-1">No sequences yet</p>
            <p className="text-[11px] text-zinc-600 mb-4">Build your first one to give Ronin Box realistic combo flow.</p>
            <Button onClick={() => setEditing({ name: "", cue_ids: [], active: true })} className="bg-[#FF331F] hover:bg-[#FF331F]/85 text-white uppercase tracking-widest font-bold text-xs">
              <Plus className="h-4 w-4 mr-2" /> Create one
            </Button>
          </div>
        ) : (
          <div className="space-y-2" data-testid="sequences-list">
            {sequences.map((s) => (
              <div key={s.id} className="border border-[#27272A] rounded-md bg-[#0A0A0A] hover:border-[#3F3F46] transition-colors" data-testid={`sequences-row-${s.id}`}>
                <div className="p-4 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-white">{s.name}</h3>
                      <span className={`text-[9px] px-2 py-0.5 rounded-sm uppercase tracking-widest font-bold ${s.active ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-700/30 text-zinc-500"}`}>
                        {s.active ? "Active" : "Off"}
                      </span>
                      <span className="text-[10px] text-zinc-600 uppercase tracking-widest">{(s.cue_ids || []).length} steps</span>
                    </div>
                    <p className="text-sm text-zinc-400 truncate">
                      {(s.cue_ids || []).map((id) => cueById[id]?.text || `[missing]`).join(" → ") || "No cues yet"}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 ml-3">
                    <button
                      onClick={() => setDiagnosingId(diagnosingId === s.id ? null : s.id)}
                      className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 inline-flex items-center gap-1 ${diagnosingId === s.id ? "text-[#FF331F]" : "text-zinc-400 hover:text-white"}`}
                      data-testid={`sequences-diagnose-${s.id}`}
                      title="Why isn't this sequence firing?"
                    >
                      <Activity className="h-3 w-3" /> Diagnose
                    </button>
                    <button onClick={() => toggleActive(s)} className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 text-zinc-400 hover:text-white" data-testid={`sequences-toggle-${s.id}`}>
                      {s.active ? "Disable" : "Enable"}
                    </button>
                    <Button variant="outline" onClick={() => setEditing(s)} className="border-[#27272A] bg-transparent text-zinc-300 h-8 px-3 text-xs uppercase tracking-widest font-bold hover:text-white" data-testid={`sequences-edit-${s.id}`}>
                      Edit
                    </Button>
                    <button onClick={() => remove(s)} className="p-2 text-zinc-500 hover:text-red-400" data-testid={`sequences-delete-${s.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {diagnosingId === s.id && (
                  <SequenceDiagnostic sequence={s} cueById={cueById} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <SequenceEditor
          sequence={editing}
          cues={cues}
          cueById={cueById}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setSequences((arr) => {
              const i = arr.findIndex((s) => s.id === saved.id);
              if (i >= 0) { const next = [...arr]; next[i] = saved; return next; }
              return [...arr, saved].sort((a, b) => a.name.localeCompare(b.name));
            });
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * Eligibility diagnostic — shows why a sequence might not be firing.
 *
 * Mirrors the runner's eligibility logic in `roninBox.js`:
 *   - Sequence must be Active
 *   - Every cue must be Active
 *   - Every cue must be tagged with the workout's mode
 *   - Every cue must be tagged with the workout's level
 *
 * For each level × mode combo, we report a green check or list the exact
 * blocking reason so the coach knows which tag to add to which cue.
 */
const ALL_LEVELS = ["beginner", "intermediate", "advanced"];
const ALL_MODES = ["shadow", "bag", "defense"];

function SequenceDiagnostic({ sequence, cueById }) {
  const cueIds = sequence.cue_ids || [];
  const missingIds = cueIds.filter((id) => !cueById[id]);
  const stepCues = cueIds.map((id) => cueById[id]).filter(Boolean);
  const inactive = stepCues.filter((c) => c.active === false);

  // For each (level, mode), check whether EVERY cue passes the filter.
  const matrix = ALL_LEVELS.flatMap((level) =>
    ALL_MODES.map((mode) => {
      const blockers = [];
      stepCues.forEach((c) => {
        const levels = c.levels || [];
        const modes = c.modes || [];
        const reasons = [];
        if (!levels.includes(level)) reasons.push("level");
        if (!modes.includes(mode)) reasons.push("mode");
        if (c.active === false) reasons.push("inactive");
        if (reasons.length) blockers.push({ cue: c, reasons });
      });
      const ok = blockers.length === 0 && missingIds.length === 0 && sequence.active !== false;
      return { level, mode, ok, blockers };
    })
  );

  const okCount = matrix.filter((m) => m.ok).length;

  return (
    <div className="border-t border-[#27272A] bg-[#070707] p-4" data-testid={`sequences-diagnostic-${sequence.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold inline-flex items-center gap-1.5">
          <Activity className="h-3 w-3" /> Eligibility diagnostic
        </div>
        <div className={`text-[10px] uppercase tracking-widest font-bold ${okCount > 0 ? "text-emerald-400" : "text-red-400"}`}>
          Fires in {okCount}/{matrix.length} combos
        </div>
      </div>

      {/* Top-level blockers that fail every combo */}
      {sequence.active === false && (
        <div className="mb-3 p-2 border border-red-700/40 bg-red-950/20 rounded-sm text-[11px] text-red-300 inline-flex items-center gap-1.5">
          <AlertTriangle className="h-3 w-3" /> Sequence is <strong className="text-red-200">disabled</strong>. Enable it (toggle above) before it can fire.
        </div>
      )}
      {missingIds.length > 0 && (
        <div className="mb-3 p-2 border border-red-700/40 bg-red-950/20 rounded-sm text-[11px] text-red-300 inline-flex items-center gap-1.5">
          <AlertTriangle className="h-3 w-3" /> {missingIds.length} step{missingIds.length === 1 ? "" : "s"} reference deleted cue{missingIds.length === 1 ? "" : "s"}: <code className="font-mono">{missingIds.join(", ")}</code>. Edit the sequence to remove them.
        </div>
      )}
      {inactive.length > 0 && (
        <div className="mb-3 p-2 border border-amber-700/40 bg-amber-950/20 rounded-sm text-[11px] text-amber-300 inline-flex items-center gap-1.5">
          <AlertTriangle className="h-3 w-3" /> {inactive.length} step{inactive.length === 1 ? "" : "s"} use an inactive cue: <span className="font-bold">{inactive.map((c) => c.text).join(", ")}</span>. Re-activate those cues in the cue admin.
        </div>
      )}

      {/* Level × mode grid */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid={`sequences-diagnostic-grid-${sequence.id}`}>
          <thead>
            <tr className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">
              <th className="text-left pb-2 pr-3">Level</th>
              {ALL_MODES.map((m) => (
                <th key={m} className="text-left pb-2 pr-3 capitalize">{m}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ALL_LEVELS.map((level) => (
              <tr key={level} className="border-t border-[#27272A]">
                <td className="py-2 pr-3 text-zinc-300 capitalize font-bold">{level}</td>
                {ALL_MODES.map((mode) => {
                  const cell = matrix.find((m) => m.level === level && m.mode === mode);
                  return (
                    <td key={mode} className="py-2 pr-3 align-top" data-testid={`diag-cell-${sequence.id}-${level}-${mode}`}>
                      {cell.ok ? (
                        <span className="text-emerald-400 inline-flex items-center gap-1 text-[11px]">
                          <Check className="h-3 w-3" /> Will fire
                        </span>
                      ) : (
                        <div className="space-y-1">
                          <span className="text-red-400 inline-flex items-center gap-1 text-[11px]">
                            <X className="h-3 w-3" /> Blocked
                          </span>
                          {cell.blockers.slice(0, 3).map((b, i) => (
                            <div key={i} className="text-[10px] text-zinc-400 leading-tight">
                              <span className="text-zinc-200 font-bold">{b.cue.text}</span>
                              <span className="text-zinc-600"> — missing </span>
                              <span className="text-red-300">{b.reasons.join(" + ")}</span>
                            </div>
                          ))}
                          {cell.blockers.length > 3 && (
                            <div className="text-[10px] text-zinc-500">+ {cell.blockers.length - 3} more</div>
                          )}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] text-zinc-600 mt-3 leading-relaxed">
        A sequence fires for a (Level × Mode) combo only when every cue in it is tagged with that level AND that mode AND active. Fix the cues listed above (in the cue admin) to unlock more combos.
      </p>
    </div>
  );
}

function SequenceEditor({ sequence, cues, cueById, onClose, onSaved }) {
  const isNew = !sequence.id;
  const [name, setName] = useState(sequence.name || "");
  const [active, setActive] = useState(sequence.active !== false);
  const [cueIds, setCueIds] = useState([...(sequence.cue_ids || [])]);
  const [addCueId, setAddCueId] = useState("");
  const [saving, setSaving] = useState(false);

  const addStep = () => {
    if (!addCueId) return;
    setCueIds((arr) => [...arr, addCueId]);
    setAddCueId("");
  };

  const removeStep = (idx) => setCueIds((arr) => arr.filter((_, i) => i !== idx));

  const moveStep = (idx, dir) => {
    setCueIds((arr) => {
      const next = [...arr];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return arr;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    if (cueIds.length === 0) { toast.error("Add at least one cue"); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), cue_ids: cueIds, active };
      const { data } = isNew
        ? await api.post("/admin/ronin-box/sequences", payload)
        : await api.patch(`/admin/ronin-box/sequences/${sequence.id}`, payload);
      onSaved(data);
      toast.success(isNew ? "Sequence created" : "Saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  // Group cues by category for the picker dropdown
  const groupedCues = useMemo(() => {
    const m = {};
    for (const c of cues) (m[c.category] = m[c.category] || []).push(c);
    return m;
  }, [cues]);

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0A0A] border-[#27272A] text-white max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white uppercase tracking-tight">
            {isNew ? "New sequence" : `Edit · ${sequence.name}`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              placeholder='e.g. "Counter combo 1"'
              className="bg-[#050505] border-[#27272A] text-white rounded-sm mt-1"
              data-testid="sequence-form-name" />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Steps · in order</label>
            {cueIds.length === 0 ? (
              <p className="text-xs text-zinc-600 italic mt-2">No steps yet — add a cue below.</p>
            ) : (
              <ol className="mt-2 space-y-1" data-testid="sequence-steps">
                {cueIds.map((id, idx) => {
                  const c = cueById[id];
                  return (
                    <li key={`${id}-${idx}`} className="flex items-center gap-2 border border-[#27272A] bg-[#050505] rounded-sm pl-2 pr-1 py-1.5" data-testid={`sequence-step-${idx}`}>
                      <span className="text-[10px] text-zinc-600 font-bold w-6 text-center">{idx + 1}</span>
                      <span className="flex-1 text-sm text-white truncate">{c ? c.text : <span className="text-red-400">[missing cue {id}]</span>}</span>
                      <span className="text-[10px] uppercase tracking-widest text-zinc-600">{c?.category}</span>
                      <div className="flex items-center">
                        <button onClick={() => moveStep(idx, -1)} disabled={idx === 0} className="p-1 text-zinc-500 hover:text-white disabled:opacity-30" data-testid={`sequence-step-up-${idx}`}>
                          <ChevronUp className="h-3 w-3" />
                        </button>
                        <button onClick={() => moveStep(idx, +1)} disabled={idx === cueIds.length - 1} className="p-1 text-zinc-500 hover:text-white disabled:opacity-30" data-testid={`sequence-step-down-${idx}`}>
                          <ChevronDown className="h-3 w-3" />
                        </button>
                        <button onClick={() => removeStep(idx)} className="p-1 text-zinc-500 hover:text-red-400" data-testid={`sequence-step-remove-${idx}`}>
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
            <div className="flex items-center gap-2 mt-3">
              <Select value={addCueId} onValueChange={setAddCueId}>
                <SelectTrigger className="bg-[#050505] border-[#27272A] text-white rounded-sm flex-1" data-testid="sequence-add-cue-select">
                  <SelectValue placeholder="Pick a cue to add…" />
                </SelectTrigger>
                <SelectContent className="bg-[#0F0F0F] border-[#27272A] text-white max-h-80">
                  {Object.entries(groupedCues).map(([cat, list]) => (
                    <div key={cat}>
                      <div className="px-2 py-1 text-[9px] uppercase tracking-widest text-zinc-600 font-bold">{cat}</div>
                      {list.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.text}</SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={addStep} disabled={!addCueId} className="bg-[#FF331F] hover:bg-[#FF331F]/85 text-white font-bold uppercase tracking-widest text-xs disabled:opacity-40" data-testid="sequence-add-step">
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>
            <p className="text-[10px] text-zinc-600 mt-2">Same cue can appear multiple times (e.g. Cross → Slip → Cross). Steps play in this order at each cue&apos;s normal time.</p>
          </div>

          <div className="flex items-center gap-2">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="accent-[#FF331F]" data-testid="sequence-form-active" />
            <label className="text-xs uppercase tracking-widest font-bold text-zinc-300">Active</label>
            <span className="text-[10px] text-zinc-600">— disabled sequences won&apos;t fire in workouts</span>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#27272A]">
            <Button variant="outline" onClick={onClose} className="border-[#27272A] bg-transparent text-zinc-300 uppercase tracking-widest font-bold text-xs" data-testid="sequence-form-cancel">
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="bg-[#FF331F] hover:bg-[#FF331F]/85 text-white font-bold uppercase tracking-widest text-xs" data-testid="sequence-form-save">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving</> : <><Save className="h-4 w-4 mr-2" /> Save</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
